import { Link } from "react-router-dom";
import { NerdStackFooter } from "../components/NerdStackFooter.jsx";
import BackArrow from "../components/BackArrow.jsx";
import { gradientFor } from "../components/toolGradients.js";
import { ToolPageBadge } from "../components/ToolPageBadge.jsx";

// ─── ToolsShell — page chrome + shared UI primitives ─────────────────
//
// Page chrome:
//   <ToolsShell title="...">{tool body}</ToolsShell>
//   The header, BackArrow, ToolPageBadge, and footer are wired for you.
//
// Sensor state:
//   <SensorNotice state={permState} what="microphone" />
//   permState is one of: "ok" | "idle" | "denied" | "unsupported" | "insecure".
//   Renders nothing for "ok"; renders the appropriate message otherwise.
//
// Styled primitives (V54+):
//   <Field label="...">{any input}</Field>       — label + input column
//   <ResultCard>{stats / content}</ResultCard>   — dark output card
//   <Stat label="..." value="..." big tone="accent" />   — label/value pair
//   <Tabs items={[{id, label}]} value={mode} onChange={setMode} />  — segmented control
//
// Raw style hooks:
//   primaryBtn — call-to-action button style
//   inputStyle — dark text-input style; for tools that style their own inputs
//
// When to use the primitives:
//   - Use <Field>/<ResultCard>/<Stat> for typical "fill form, get result"
//     tools (calculators, converters, generators). They normalize the
//     chrome so all tools look like family without duplicating styles.
//   - Skip them when a tool has a custom layout the primitives don't fit
//     (e.g. side-by-side fraction inputs, live camera views, canvas
//     widgets). The primitives are additive — they don't have to be
//     used everywhere.
//   - Future tools should reach for the primitives first and only fall
//     back to inline styles when the primitives don't fit. This keeps
//     the family coherent without forcing a one-size-fits-all API.

// NerdTools accent = darkened red, per the LOCKED icon-derived rule:
// in-app accent = icon gradient START hue. T mark = darkened-red -> violet.
// (NerdTools is its own strategic thread, not a subject suite, but the
// icon/accent system still applies uniformly.)
export const TOOLS_ACCENT = "#E0584E";
export const VIOLET = "#7C3AED";

export function ToolsShell({ title, children, back = "/tools" }) {
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "#0A0B14",
        color: "#E8E9F3",
        display: "flex",
        flexDirection: "column",
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <header
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          padding: "16px 18px",
          borderBottom: "1px solid #1C1E2E",
          position: "sticky",
          top: 0,
          background: "#0A0B14",
          zIndex: 10,
        }}
      >
        <BackArrow to={back} from={TOOLS_ACCENT} toColor={gradientFor(title, "/tools/x").from} />
        <ToolPageBadge name={title} family="tools" accent={TOOLS_ACCENT} />
        <h1 style={{ fontSize: 17, fontWeight: 600, margin: 0 }}>{title}</h1>
      </header>
      <main style={{ flex: 1, padding: "clamp(20px, 3vw, 36px) clamp(18px, 4vw, 40px)", maxWidth: "min(900px, 92vw)", width: "100%", margin: "0 auto" }}>
        {children}
      </main>
      <footer style={{ padding: "14px 18px", borderTop: "1px solid #1C1E2E" }}>
        <NerdStackFooter variant="tool" family="tools" />
      </footer>
    </div>
  );
}

// Standard graceful state for sensor permission / availability.
export function SensorNotice({ state, what }) {
  if (state === "ok") return null;
  const msg =
    state === "denied"
      ? `${what} permission was denied. Enable it in your browser/site settings and reload.`
      : state === "unsupported"
      ? `${what} isn't available on this device or browser.`
      : state === "insecure"
      ? `${what} needs a secure (HTTPS) connection.`
      : `Tap “Enable” and allow ${what} access when prompted.`;
  return (
    <div style={{ background: "#1F1418", border: "1px solid #E0584E33", borderRadius: 12, padding: "14px 16px", fontSize: 13, color: "#D9A39E", marginTop: 14 }}>
      {msg}
    </div>
  );
}

export const primaryBtn = {
  background: TOOLS_ACCENT,
  color: "#1A0604",
  fontWeight: 700,
  fontSize: 15,
  border: "none",
  borderRadius: 12,
  padding: "13px 22px",
  cursor: "pointer",
};

// ─── V54 shared UI primitives ────────────────────────────────────────
// Lightweight, "dumb" wrappers that normalize the styling patterns that
// were duplicated inline across the 37 V53 tools. Each component is
// minimal-prop: it owns the appearance, the tool owns the input logic
// and state. This shape preserves each tool's idiosyncratic input
// patterns (radio groups, sliders, custom canvases) while standardizing
// the chrome.
//
// The inline `inputStyle` constant is also exported for tools that want
// to style their own <input> elements consistently.

// Shared input style. Matches the pre-V54 inline definitions exactly.
export const inputStyle = {
  width: "100%",
  background: "#13141F",
  border: "1px solid #262839",
  borderRadius: 10,
  color: "#E8E9F3",
  fontSize: 15,
  padding: "12px 14px",
  boxSizing: "border-box",
};

// <Field label="..."> wraps a child input with the standard label.
// Pass any input/select/textarea/custom node as children.
export function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {label && (
        <div style={{ fontSize: 13, color: "#9A9DBE", marginBottom: 8 }}>
          {label}
        </div>
      )}
      {children}
      {hint && (
        <div style={{ fontSize: 12, color: "#6F7290", marginTop: 6 }}>
          {hint}
        </div>
      )}
    </div>
  );
}

// <ResultCard>...</ResultCard> — the dark output card. Tools put one
// or more <Stat>s and/or arbitrary content inside.
export function ResultCard({ children, error }) {
  return (
    <div
      style={{
        background: "#13141F",
        border: `1px solid ${error ? "#F8716A" : TOOLS_ACCENT}33`,
        borderRadius: 12,
        padding: "18px 20px",
        marginTop: 14,
      }}
    >
      {children}
    </div>
  );
}

// <Stat label="..." value="..." big? tone? mono? /> — label + value pair.
// `big`: render value at the headline size (use for the primary result).
// `tone="accent"`: render value in the suite accent color.
// `mono`: tabular-numeric font for aligned digits.
export function Stat({ label, value, big = false, tone, mono = true }) {
  const valueStyle = {
    fontSize: big ? 36 : 17,
    fontWeight: big ? 800 : 700,
    color: tone === "accent" ? TOOLS_ACCENT : "#E8E9F3",
    fontVariantNumeric: mono ? "tabular-nums" : "normal",
    lineHeight: big ? 1.1 : 1.3,
  };
  return (
    <div style={{ marginBottom: big ? 6 : 0 }}>
      {label && (
        <div
          style={{
            fontSize: big ? 13 : 11,
            color: big ? "#9A9DBE" : "#7C8099",
            marginBottom: big ? 10 : 2,
            textTransform: big ? "none" : "uppercase",
            letterSpacing: big ? 0 : 0.4,
          }}
        >
          {label}
        </div>
      )}
      <div style={valueStyle}>{value}</div>
    </div>
  );
}

// <Tabs items={[{id, label}]} value selected setValue /> — segmented
// control for tools with multiple modes. Used by Level Pro and any
// multi-mode tool added later.
export function Tabs({ items, value, onChange }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 4,
        background: "#1C1E2E",
        padding: 4,
        borderRadius: 10,
        marginBottom: 18,
        overflowX: "auto",
      }}
    >
      {items.map((it) => {
        const active = it.id === value;
        return (
          <button
            key={it.id}
            onClick={() => onChange(it.id)}
            style={{
              flex: "1 1 auto",
              background: active ? TOOLS_ACCENT : "transparent",
              color: active ? "#1A0604" : "#9A9DBE",
              border: "none",
              borderRadius: 8,
              padding: "9px 12px",
              fontSize: 13,
              fontWeight: 600,
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {it.label}
          </button>
        );
      })}
    </div>
  );
}

